import type { Auth, User, UserCredential } from 'firebase/auth';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification,
} from 'firebase/auth';

export type SignUpWithEmailPayload = {
  readonly email: string;
  readonly password: string;
  readonly displayName: string;
};

export type SignInWithEmailPayload = {
  readonly email: string;
  readonly password: string;
};

/**
 * Create new user account with email and password
 */
export const signUpWithEmail = async (
  auth: Auth,
  payload: SignUpWithEmailPayload,
): Promise<UserCredential> => {
  // Create account
  const userCredential = await createUserWithEmailAndPassword(
    auth,
    payload.email,
    payload.password,
  );

  // Update display name
  if (payload.displayName && userCredential.user) {
    await updateProfile(userCredential.user, {
      displayName: payload.displayName,
    });

    // Reload user to ensure auth state has updated profile
    await userCredential.user.reload();
  }

  // Send verification email
  await sendEmailVerification(userCredential.user);

  return userCredential;
};

/**
 * Sign in with email and password
 */
export const signInWithEmail = async (
  auth: Auth,
  payload: SignInWithEmailPayload,
): Promise<UserCredential> => {
  return await signInWithEmailAndPassword(auth, payload.email, payload.password);
};

/**
 * Send password reset email
 */
export const sendPasswordReset = async (auth: Auth, email: string): Promise<void> => {
  await sendPasswordResetEmail(auth, email);
};

/**
 * Resend email verification
 */
export const resendEmailVerification = async (user: User): Promise<void> => {
  if (!user.emailVerified) {
    await sendEmailVerification(user);
  }
};
